import type {
  CreateWebsiteBlogPostRequest,
  UpdateWebsiteBlogPostRequest,
  WebsiteBlogCategoryOption,
  WebsiteBlogDestinationOption,
  WebsiteBlogPostDetail,
  WebsiteBlogPostListItem,
  WebsiteBlogThemeOption,
} from '@utazzo/shared';

import { apiClient } from '@/lib/api-client';

/**
 * Admin Website Blog API. Mirrors pages.api.ts (shared axios client +
 * react-query in the page). The backend returns the standard
 * `{ data, pagination }` list envelope and the WebsiteBlogPost shapes.
 */

export interface BlogListQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  categoryId?: string;
  destinationId?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface BlogListResponse {
  data: WebsiteBlogPostListItem[];
  pagination: { page: number; pageSize: number; totalItems: number };
}

const BASE = '/admin/website/blogs';

export const listBlogPosts = async (query: BlogListQuery): Promise<BlogListResponse> => {
  const { data } = await apiClient.get<BlogListResponse>(BASE, { params: query });
  return data;
};

export const getBlogPost = async (id: string): Promise<WebsiteBlogPostDetail> => {
  const { data } = await apiClient.get<WebsiteBlogPostDetail>(`${BASE}/${id}`);
  return data;
};

export const listBlogCategories = async (): Promise<WebsiteBlogCategoryOption[]> => {
  const { data } = await apiClient.get<WebsiteBlogCategoryOption[]>(`${BASE}/categories`);
  return data;
};

export const listBlogDestinations = async (): Promise<WebsiteBlogDestinationOption[]> => {
  const { data } = await apiClient.get<WebsiteBlogDestinationOption[]>(`${BASE}/destinations`);
  return data;
};

export const listBlogThemes = async (): Promise<WebsiteBlogThemeOption[]> => {
  const { data } = await apiClient.get<WebsiteBlogThemeOption[]>(`${BASE}/themes`);
  return data;
};

export const createBlogPost = async (
  payload: CreateWebsiteBlogPostRequest,
): Promise<WebsiteBlogPostDetail> => {
  const { data } = await apiClient.post<WebsiteBlogPostDetail>(BASE, payload);
  return data;
};

export const updateBlogPost = async (
  id: string,
  payload: UpdateWebsiteBlogPostRequest,
): Promise<WebsiteBlogPostDetail> => {
  const { data } = await apiClient.patch<WebsiteBlogPostDetail>(`${BASE}/${id}`, payload);
  return data;
};

export const publishBlogPost = async (id: string): Promise<WebsiteBlogPostDetail> => {
  const { data } = await apiClient.post<WebsiteBlogPostDetail>(`${BASE}/${id}/publish`);
  return data;
};

export const unpublishBlogPost = async (id: string): Promise<WebsiteBlogPostDetail> => {
  const { data } = await apiClient.post<WebsiteBlogPostDetail>(`${BASE}/${id}/unpublish`);
  return data;
};

export const deleteBlogPost = async (id: string): Promise<void> => {
  await apiClient.delete(`${BASE}/${id}`);
};
